import {
  ApiAttributionModel,
  ApiChartAttribute,
  ApiChartHaving,
  ApiChartRank,
  ApiDimension,
  ApiMetric,
} from "@incendium/api";
import { ExploreConfig, TExploreConfig } from "features/explorer/types";
import useAppendSearchState from "Hooks/useAppendSearchState";
import { CallbackOrVal } from "Interfaces";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { usePrevious, useUpdateEffect } from "react-use";

interface IExplorerContext<T extends string> {
  rankingIndex: number;
  setRankingIndex: React.Dispatch<React.SetStateAction<number>>;
  dimension: ApiDimension;
  setDimension: React.Dispatch<React.SetStateAction<ApiDimension>>;
  selectedRanks: ApiChartRank[];
  setSelectedRanks: React.Dispatch<React.SetStateAction<ApiChartRank[]>>;
  having: ApiChartHaving[];
  setHaving: React.Dispatch<React.SetStateAction<ApiChartHaving[]>>;
  config: ExploreConfig;
  configs: TExploreConfig<string>;
  attributes: ApiChartAttribute[];
  setAttributes: React.Dispatch<React.SetStateAction<ApiChartAttribute[]>>;
  selectedAttributionTypes: ApiAttributionModel[];
  setSelectedAttributionTypes: React.Dispatch<
    React.SetStateAction<ApiAttributionModel[]>
  >;
  type: T;
  setType: (v: CallbackOrVal<T>) => void;
}

export const ExplorerContext = createContext<IExplorerContext<any>>({
  rankingIndex: 0,
  setRankingIndex: () => null,
  dimension: ApiDimension.DIMENSION_NOT_SET,
  setDimension: () => null,
  selectedRanks: [],
  setSelectedRanks: () => null,
  having: [],
  setHaving: () => null,
  attributes: [],
  setAttributes: () => null,
  selectedAttributionTypes: [],
  setSelectedAttributionTypes: () => null,
  config: {
    title: "",
    desc: "",
    ranking: [],
  },
  configs: {
    "": {
      title: "",
      desc: "",
      ranking: [],
    },
  },
  type: "",
  setType: () => null,
});

export function useExplorerContext<T extends string>() {
  const ctx = useContext(ExplorerContext) as IExplorerContext<T> | undefined;
  if (!ctx) {
    throw new Error("useExplorer must be inside an ExplorerProvider");
  }
  return ctx;
}

interface IExplorerProviderProps<T extends string> {
  children: React.ReactNode;
  configs: TExploreConfig<T>;
  type: T;
}

const ExplorerProvider = <T extends string>({
  children,
  configs,
  type: initialType,
}: IExplorerProviderProps<T>) => {
  const [rankingIndex, setRankingIndex] = useState(0);
  const [type, setType] = useAppendSearchState("expl", initialType);
  const prevType = usePrevious(type);
  const currentType = type ?? initialType;
  const config = configs[currentType];
  const [dimension, setDimension] = useState<ApiDimension>(
    config.analyseBy?.grouping[0]?.dimensions?.[0] ||
      ApiDimension.DIMENSION_NOT_SET,
  );
  const [selectedRanks, setSelectedRanks] = useState<ApiChartRank[]>([]);
  const [having, setHaving] = useState<ApiChartHaving[]>([]);
  const [attributes, setAttributes] = useState<ApiChartAttribute[]>(
    config.attributes || [],
  );
  const [selectedAttributionTypes, setSelectedAttributionTypes] = useState<
    ApiAttributionModel[]
  >([]);

  useEffect(() => {
    if (type === prevType) {
      return;
    }

    setRankingIndex(0);
    setSelectedRanks(
      config.ranking[0]
        ? (config.ranking[0].ranks || []).filter((r) =>
            (config.default || []).includes(r.metric as ApiMetric),
          )
        : [],
    );
    setHaving(config.having?.havings || []);
    setAttributes(config.attributes || []);

    setDimension(
      config.analyseBy?.grouping[0].dimensions[0] ||
        ApiDimension.DIMENSION_NOT_SET,
    );
  }, [config, type, prevType]);

  const configDimensions = useMemo(() => {
    if (!config.analyseBy?.grouping) return new Set<ApiDimension>();
    return new Set(
      config.analyseBy.grouping.flatMap((g) =>
        Array.isArray(g.dimensions) ? g.dimensions : [],
      ),
    );
  }, [config]);

  useUpdateEffect(() => {
    const firstDimension =
      config.analyseBy?.grouping[0]?.dimensions?.[0] ??
      ApiDimension.DIMENSION_NOT_SET;
    setDimension((current) =>
      configDimensions.has(current) ? current : firstDimension,
    );
    setAttributes(config.attributes || []);
  }, [config, configDimensions]);

  useUpdateEffect(() => {
    setSelectedRanks(
      config.ranking[rankingIndex]
        ? (config.ranking[rankingIndex].ranks || []).filter((r) =>
            (config.default || []).includes(r.metric as ApiMetric),
          )
        : [],
    );
  }, [rankingIndex]);

  const state = useMemo(
    () => ({
      dimension,
      setDimension,
      selectedRanks,
      setSelectedRanks,
      having,
      setHaving,
      attributes,
      setAttributes,
      selectedAttributionTypes,
      setSelectedAttributionTypes,
      config,
      configs,
      rankingIndex,
      setRankingIndex,
      type: type || "",
      setType,
    }),
    [
      dimension,
      setDimension,
      selectedRanks,
      setSelectedRanks,
      having,
      setHaving,
      attributes,
      setAttributes,
      selectedAttributionTypes,
      setSelectedAttributionTypes,
      config,
      configs,
      rankingIndex,
      setRankingIndex,
      setType,
      type,
    ],
  );

  return (
    <ExplorerContext.Provider value={state}>
      {children}
    </ExplorerContext.Provider>
  );
};

export default ExplorerProvider;
